/**
 * UserListView v2.0 -- User management + skill progress per user.
 * Shows user cards with skill chips, expandable detail with tabs: Skills | Memoria.
 * Ref: AGENT-FEAT-001 task F5
 *
 * @module presentation/views/UserListView
 */

import { UserApiClient } from '../../infrastructure/api/UserApiClient.js';
import { AgentMemoryApiClient } from '../../infrastructure/api/AgentMemoryApiClient.js';
import { LearningApiClient } from '../../infrastructure/api/LearningApiClient.js';
import { LearningProfileApiClient } from '../../infrastructure/api/LearningProfileApiClient.js';
import { AgentBadgeApiClient } from '../../infrastructure/api/AgentBadgeApiClient.js';
import { esAgente, separarPersonasDeAgentes, nombreVisible, porQueNoEsPersona, sujetoDe } from '../../application/usuarios/personasYAgentes.js';
import { rutaDeLaFicha } from '../../application/personas/fichaUnica.js';
import { avisar, confirmar } from '../shared/nxDialogo.js';

const ROLES = ['user', 'admin', 'manager', 'viewer'];

const ROLE_COLORS = {
  admin: '#f85149',
  manager: '#d29922',
  user: '#58a6ff',
  viewer: '#6e7681'
};

const STATUS_COLORS = {
  active: '#238636',
  inactive: '#6e7681'
};

const CAT_COLORS = {
  marketing: '#f97583',
  productividad: '#58a6ff',
  nexo: '#238636',
  gobernanza: '#d29922',
  agente: '#a371f7'
};

// Deuda 4: el escape local solo cambiaba "<" y este archivo lo usa dentro de atributos con comilla doble.
import { esc } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

function badge(label, colorMap) {
  const color = colorMap[label] || '#6e7681';
  return `<span class="status-badge" style="background:${color}22;color:${color}">${label}</span>`;
}

// SUJ-T2 (b): la frontera persona/agente vive en application/usuarios/personasYAgentes.js
// (rol 'agent', nombre "(Agente)" o codigo de agente); antes solo miraba el nombre.
const isAgentUser = esAgente;

function userRow(u) {
  const nombre = nombreVisible(u);
  const initials = nombre.substring(0, 2).toUpperCase();
  const alta = u.createdAt ? formatearFecha(u.createdAt, {}, { locale: 'es' }) : '';
  return `
    <div class="artifact-item user-row" data-id="${esc(u.id)}">
      <div class="task-info">
        <div class="user-avatar">${esc(initials)}</div>
        <div>
          <div class="artifact-item-title editable-name" data-id="${esc(u.id)}" data-name="${esc(u.displayName)}" title="Click para editar nombre">${esc(nombre)}</div>
          <div class="artifact-item-meta">
            ${[u.email, alta].filter(Boolean).map(esc).join(' &middot; ')}
            <span class="user-skill-chip" data-uid="${u.id}" style="margin-left:8px;font-size:0.875rem;color:#6e7681">...</span>
          </div>
        </div>
      </div>
      <div class="task-actions">
        ${badge(u.role, ROLE_COLORS)}
        <select class="status-select role-select" data-id="${u.id}" title="Cambiar rol">
          <option value="" disabled selected>Rol</option>
          ${ROLES.filter(r => r !== u.role).map(r => `<option value="${r}">${r}</option>`).join('')}
        </select>
        ${u.status === 'active'
          ? `<button class="btn-icon btn-delete" data-id="${u.id}" title="Desactivar">&times;</button>`
          : badge(u.status, STATUS_COLORS)}
      </div>
    </div>`;
}

// ─── Lazy skill summaries (non-blocking) ─────────────────────────────────────

async function loadSkillSummaries(items) {
  const skillsRes = await LearningApiClient.getSkills();
  const totalSkills = skillsRes.ok ? (skillsRes.data?.data || []).length : 13;

  const results = await Promise.allSettled(
    items.map(u => LearningApiClient.getSkillProgress(u.id).then(res => ({ id: u.id, res })))
  );

  for (const r of results) {
    if (r.status !== 'fulfilled') continue;
    const { id, res } = r.value;
    const chip = document.querySelector(`.user-skill-chip[data-uid="${id}"]`);
    if (!chip) continue;

    if (!res.ok) { chip.textContent = '\u2014'; continue; }

    const skills = res.data?.data || [];
    let unlocked = 0, nxs = 0;
    for (const s of skills) {
      const answered = parseInt(s.answered) || 0;
      const accuracy = parseFloat(s.accuracy) || 0;
      const required = parseInt(s.questions_required) || 10;
      const reqAcc = parseInt(s.accuracy_required) || 70;
      if (answered >= required && accuracy >= reqAcc) {
        unlocked++;
        nxs += parseInt(s.nexos_reward) || 0;
      }
    }
    chip.textContent = `${unlocked}/${totalSkills} skills \u00B7 ${nxs} NXS`;
    chip.style.color = unlocked > 0 ? '#238636' : '#6e7681';
  }
}

// ─── Render Agent Badges tab (for agent users) ─────────────────────────────

const BADGE_COLORS = { memoria: '#a371f7', consulta: '#58a6ff', rag: '#238636', diagnostico: '#d29922' };
const BADGE_VALIDATION = { 'agent-quality-test': 'Test AQ', 'capability-proof': 'Verificado', 'manual': 'Manual' };

async function renderAgentBadgesTab($el, userId) {
  $el.innerHTML = '<span style="color:#8b949e">Cargando badges...</span>';
  const agentCode = userId?.toUpperCase().replace(/[^A-Z0-9-]/g, '');
  const res = await AgentBadgeApiClient.list({ agentCode });
  if (!res.ok) { $el.innerHTML = '<span style="color:#f85149">Error cargando badges</span>'; return; }
  const badges = res.data?.data?.items || res.data?.data || [];
  if (badges.length === 0) { $el.innerHTML = '<span style="color:#484f58">Sin badges registrados</span>'; return; }

  const cards = badges.map(b => {
    const color = BADGE_COLORS[b.domain] || '#6e7681';
    const valLabel = BADGE_VALIDATION[b.validationMethod || b.validation_method] || 'Manual';
    const unlocked = b.unlockedAt || b.unlocked_at;
    const dateStr = unlocked ? formatearFecha(unlocked, { day: 'numeric', month: 'short', year: 'numeric' }) : '';
    const caps = b.capabilityCodes || b.capability_codes || [];
    const capsCount = Array.isArray(caps) ? caps.length : 0;

    return `
      <div style="padding:8px 10px;background:#161b22;border-radius:6px;border-left:3px solid ${color}">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <div>
            <span style="font-weight:600;font-size:0.875rem">${esc(b.name)}</span>
            <span class="task-tag" style="background:${color}22;color:${color};margin-left:4px;font-size:0.875rem">${esc(b.domain)}</span>
          </div>
          <div style="text-align:right;font-size:0.875rem">
            <span class="version-badge">v${esc(b.version || '1.0')}</span>
            <span style="color:#6e7681;margin-left:4px">${esc(valLabel)}</span>
          </div>
        </div>
        <div style="display:flex;justify-content:space-between;margin-top:4px;font-size:0.875rem;color:#6e7681">
          <span>${capsCount > 0 ? `${capsCount} capacidades` : ''}</span>
          <span>${dateStr}</span>
        </div>
      </div>`;
  }).join('');

  $el.innerHTML = `
    <div style="display:flex;gap:16px;margin-bottom:10px;font-size:0.875rem">
      <span><strong style="color:#a371f7">${badges.length}</strong> badges ganados</span>
    </div>
    <div style="display:flex;flex-direction:column;gap:6px">${cards}</div>`;
}

// ─── Render Skills tab ───────────────────────────────────────────────────────

async function renderSkillsTab($el, userId, isAgent) {
  if (isAgent) return renderAgentBadgesTab($el, userId);

  $el.innerHTML = '<span style="color:#8b949e">Cargando skills...</span>';

  const [progressRes, skillsRes] = await Promise.all([
    LearningApiClient.getSkillProgress(userId),
    LearningApiClient.getSkills()
  ]);

  if (!progressRes.ok) {
    $el.innerHTML = `<span style="color:#f85149">Error: ${esc(progressRes.error)}</span>`;
    return;
  }

  const progress = progressRes.data?.data || [];
  const allSkills = skillsRes.ok ? (skillsRes.data?.data || []) : [];
  const skillMap = new Map(allSkills.map(s => [s.code, s]));

  let unlocked = 0, totalNxs = 0, totalAnswered = 0;
  for (const s of progress) {
    const answered = parseInt(s.answered) || 0;
    const accuracy = parseFloat(s.accuracy) || 0;
    const required = parseInt(s.questions_required) || 10;
    const reqAcc = parseInt(s.accuracy_required) || 70;
    totalAnswered += answered;
    if (answered >= required && accuracy >= reqAcc) {
      unlocked++;
      totalNxs += parseInt(s.nexos_reward) || 0;
    }
  }

  const skillCards = progress.map(s => {
    const answered = parseInt(s.answered) || 0;
    const accuracy = parseFloat(s.accuracy) || 0;
    const required = parseInt(s.questions_required) || 10;
    const reqAcc = parseInt(s.accuracy_required) || 70;
    const nxs = parseInt(s.nexos_reward) || 0;
    const pct = Math.min(100, Math.round((answered / required) * 100));
    const ok = answered >= required && accuracy >= reqAcc;
    const color = CAT_COLORS[s.category] || '#6e7681';

    const catalogSkill = skillMap.get(s.code);
    const instrCodes = isAgent && catalogSkill
      ? (Array.isArray(catalogSkill.instruction_codes) ? catalogSkill.instruction_codes
        : (typeof catalogSkill.instruction_codes === 'string' ? (() => { try { return JSON.parse(catalogSkill.instruction_codes); } catch { return []; } })() : []))
      : [];

    return `
      <div style="padding:8px 10px;background:#161b22;border-radius:6px;border-left:3px solid ${color}">
        <div style="display:flex;justify-content:space-between;align-items:center">
          <div>
            <span style="font-weight:600;font-size:0.875rem">${esc(s.name)}</span>
            <span class="task-tag" style="background:${color}22;color:${color};margin-left:4px;font-size:0.875rem">${esc(s.category)}</span>
          </div>
          <div style="text-align:right;font-size:0.875rem">
            ${ok ? '<span style="color:#238636;font-weight:700">\u2713</span>' : `<span style="color:#6e7681">${answered}/${required}</span>`}
            <span style="color:#d29922;margin-left:4px">+${nxs}</span>
          </div>
        </div>
        <div style="margin-top:6px;height:4px;background:#21262d;border-radius:2px;overflow:hidden">
          <div style="width:${pct}%;height:100%;background:${ok ? '#238636' : color};border-radius:2px"></div>
        </div>
        <div style="display:flex;justify-content:space-between;margin-top:3px;font-size:0.875rem;color:#6e7681">
          <span>${pct}%</span>
          <span>Precision: ${accuracy}%${accuracy >= reqAcc ? ' \u2713' : ''}</span>
        </div>
        ${instrCodes.length > 0 ? `<div style="margin-top:4px;font-size:0.875rem;color:#8b949e">INS: ${instrCodes.map(c => `<code style="font-size:0.875rem">${esc(c)}</code>`).join(' ')}</div>` : ''}
      </div>`;
  }).join('');

  $el.innerHTML = `
    <div style="display:flex;gap:16px;margin-bottom:10px;font-size:0.875rem">
      <span><strong style="color:#238636">${unlocked}</strong>/${allSkills.length} desbloqueados</span>
      <span style="color:#d29922">${totalNxs} NXS ganados</span>
      <span style="color:#6e7681">${totalAnswered} respuestas</span>
    </div>
    <div style="display:flex;flex-direction:column;gap:6px">
      ${skillCards || '<span style="color:#484f58">Sin progreso registrado</span>'}
    </div>`;
}

// ─── Render Memoria tab (refactored from existing code) ──────────────────────

/**
 * PESTANA APRENDIZAJE (CTX-P2) -- lo que el TUTOR sabe de esta persona.
 *
 * AUDIENCIA (regla #5638): JOSSE, el dueno, en el panel de escritorio. Es donde
 * responde "como va esta persona" sin tener que acordarse de en cual de tres
 * pantallas estaba el dato.
 *
 * POR QUE ESTA AQUI Y NO EN #/contexto. Hasta hoy el perfil del tutor se
 * validaba en la pantalla de accesos, y ahi habia que ESCRIBIR A MANO el UUID
 * del sujeto; su propio comentario lo admitia: "No hay lista de personas
 * todavia". En Usuarios ese problema desaparece solo, porque la fila ya sabe de
 * quien habla. Decision D10 del PLAN_2026-09-07.
 *
 * NO DUPLICA A LA PESTANA SKILLS, y conviene saber por que: Skills muestra el
 * modelo VIEJO de quiz (respondidas, precision, NXS). Esto muestra el del
 * tutor: nivel, tema actual, temas dominados, las sesiones y las observaciones
 * que esperan una decision. Son dos cosas distintas con la misma palabra.
 *
 * LO QUE HOY NO SE PUEDE, dicho de frente en la propia pantalla: resolver el
 * sujeto de OTRA persona. La API resuelve el propio (por el email del JWT) y no
 * publica un endpoint que enumere sujetos -- medido: /learning/subjects da 404,
 * con control positivo (/api/v1/bitacora 200) y control negativo de la misma
 * forma (404). El dia que ese endpoint exista, esta pestana se enciende sola.
 */

// Las clases de observacion, con los valores REALES de la base (medido el
// 7-sep: kind=estimate 12 filas en proposed, kind=objective 2 en accepted).
const CLASE_OBS = {
  objective: { txt: 'objetivo', color: '#238636' },
  estimate: { txt: 'estimacion', color: '#d29922' },
  validated: { txt: 'validado', color: '#58a6ff' }
};

function fechaCortaApr(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return formatearFecha(d, { day: '2-digit', month: 'short' })
    + ' ' + formatearFecha(d, { hour: '2-digit', minute: '2-digit' }, { parte: 'hora' });
}

/**
 * El nivel NO es un texto: en la base es un JSON como
 * {"level":"A2","framework":"CEFR","source":"auto-reporte de Josse"}.
 * Pintarlo crudo mostraria llaves y comillas en la cara del dueno.
 */
function nivelLegible(nivel) {
  if (!nivel) return '';
  if (typeof nivel === 'string') return nivel;
  const l = nivel.level ?? nivel.Level ?? '';
  const f = nivel.framework ?? nivel.Framework ?? '';
  return [l, f].filter(Boolean).join(' ');
}

/**
 * EL CORREO DE LA SESION, sacado del propio token.
 *
 * El panel guarda tres cosas en localStorage -- nx_auth_token, nx_env y
 * nx_role -- y NINGUNA es el correo (comprobado en config.js). Pero el JWT lo
 * lleva dentro, asi que se lee de ahi.
 *
 * NO se verifica la firma A PROPOSITO, y no es un descuido: esto solo decide
 * QUE FILA se marca como propia. Quien manda de verdad es la API, que resuelve
 * el sujeto por el email del token que ella misma valida. Si alguien falsea
 * este dato en su navegador, lo unico que consigue es que le salga un aviso.
 */
function correoDeLaSesion() {
  try {
    const t = localStorage.getItem('nx_auth_token');
    if (!t || t.split('.').length !== 3) return '';
    const payload = JSON.parse(atob(t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
    return String(payload.email || payload.sub || '').toLowerCase();
  } catch { return ''; }
}

/** El aviso que explica POR QUE no hay nada, que no es lo mismo que no haber nada. */
function avisoApr(titulo, detalle) {
  return '<div style="padding:14px;border:1px dashed #30363d;border-radius:6px;color:#8b949e">'
    + '<div style="color:#e1e4e8;font-weight:600;margin-bottom:4px">' + esc(titulo) + '</div>'
    + '<div style="font-size:0.875rem;line-height:1.5">' + esc(detalle) + '</div>'
    + '</div>';
}

function perfilAprHtml(p, programa) {
  if (!p) {
    return avisoApr('Todavia no hay perfil',
      'El tutor no ha cerrado ninguna sesion en ' + programa + ' para esta persona.');
  }
  const fila = (k, v) => '<div style="display:flex;gap:8px;margin-bottom:3px">'
    + '<span style="color:#8b949e;min-width:112px">' + esc(k) + '</span>'
    + '<span style="color:#e1e4e8">' + esc(v) + '</span></div>';
  const confirmado = p.validadoEl
    ? 'confirmado ' + fechaCortaApr(p.validadoEl) + (p.validadoPor ? ' por ' + p.validadoPor : '')
    : 'sin confirmar';
  return '<div style="background:#161b22;border:1px solid #30363d;border-radius:6px;padding:12px;margin-bottom:12px">'
    + fila('Nivel', nivelLegible(p.nivel) || 'sin definir')
    + fila('Tema actual', p.tema || 'sin tema')
    // LOS TEMAS DOMINADOS SE DICEN AUNQUE SEAN CERO, y con su causa. Medido el
    // 7-sep: hay 6 observaciones de topic_mastery y topics_mastered vacio,
    // porque ninguna se ha confirmado. Un "0" a secas se leeria como "no
    // aprendio nada", que es lo contrario de lo que pasa.
    + fila('Temas dominados', p.dominados && p.dominados.length
      ? p.dominados.join(', ')
      : 'ninguno confirmado todavia (lo estimado esta abajo, sin decidir)')
    + fila('Version', 'v' + (p.version ?? '?') + ' - ' + confirmado)
    + '</div>';
}

function sesionesAprHtml(sesiones) {
  if (!sesiones.length) {
    return '<div style="color:#6e7681;font-size:0.875rem;margin-bottom:12px">Ninguna sesion con el tutor todavia.</div>';
  }
  const filas = sesiones.slice(0, 8).map((s) => {
    const dur = s.minutos ? s.minutos + ' min' : '--';
    return '<div style="display:flex;justify-content:space-between;padding:5px 0;border-bottom:1px solid #21262d">'
      + '<span style="color:#e1e4e8">' + esc(fechaCortaApr(s.desde) || 'sin fecha') + '</span>'
      + '<span style="color:#8b949e">' + esc(s.estado || '') + '</span>'
      + '<span style="color:#8b949e">' + esc(dur) + '</span>'
      + '</div>';
  }).join('');
  const mas = sesiones.length > 8
    ? '<div style="color:#6e7681;font-size:0.875rem;margin-top:4px">y ' + (sesiones.length - 8) + ' mas</div>'
    : '';
  return '<div style="margin-bottom:12px">'
    + '<div style="color:#8b949e;font-size:0.875rem;margin-bottom:6px">SESIONES CON EL TUTOR (' + sesiones.length + ')</div>'
    + filas + mas + '</div>';
}

function observacionesAprHtml(pendientes) {
  if (!pendientes.length) {
    return '<div style="color:#6e7681;font-size:0.875rem">Nada espera tu decision ahora mismo.</div>';
  }
  const filas = pendientes.map((o) => {
    const c = CLASE_OBS[o.tipo] ?? { txt: o.tipo || 'sin clase', color: '#8b949e' };
    const conf = (o.confianza !== null && o.confianza !== undefined && !Number.isNaN(o.confianza))
      ? ' - confianza ' + Math.round(Number(o.confianza) * 100) + '%' : '';
    return '<div style="padding:7px 0;border-bottom:1px solid #21262d">'
      + '<span style="color:' + c.color + ';font-weight:600;font-size:0.875rem;text-transform:uppercase">' + esc(c.txt) + '</span>'
      + '<span style="color:#e1e4e8;margin-left:8px">' + esc(o.metrica || '') + '</span>'
      + '<span style="color:#8b949e;margin-left:6px">' + esc(o.tema || '') + esc(conf) + '</span>'
      + (o.valor ? '<div style="color:#8b949e;font-size:0.875rem;margin-top:2px">' + esc(o.valor) + '</div>' : '')
      + '</div>';
  }).join('');
  return '<div>'
    + '<div style="color:#8b949e;font-size:0.875rem;margin-bottom:6px">LO QUE EL TUTOR PROPONE (' + pendientes.length + ')</div>'
    + filas
    + '<div style="color:#6e7681;font-size:0.875rem;margin-top:8px">'
    + 'Confirmar o descartar cada una se hace en Accesos de asistentes.</div>'
    + '</div>';
}

async function renderAprendizajeTab($el, esMiFila) {
  $el.innerHTML = '<span style="color:#8b949e">Cargando...</span>';

  // SOLO LA PROPIA FILA, y se dice por que. La API resuelve el sujeto de quien
  // llama (email del JWT -> users -> context_subjects, en SubjectResolver) y no
  // publica forma de enumerar los de otros. Prometer aqui el progreso de un
  // tercero seria prometer lo que no se puede cumplir.
  if (!esMiFila) {
    $el.innerHTML = avisoApr('Esto solo se ve en tu propia fila',
      'La API resuelve el aprendizaje de quien tiene la sesion abierta, y todavia '
      + 'no publica una forma de pedir el de otra persona. En cuanto exista, esta '
      + 'pestana lo muestra sin tocar nada mas.');
    return;
  }

  const quien = await LearningProfileApiClient.whoami();
  if (!quien.ok) {
    $el.innerHTML = avisoApr('No pudimos saber tu perfil de aprendizaje', quien.detalle || quien.motivo);
    return;
  }
  if (!quien.sujetoId) {
    $el.innerHTML = avisoApr('Tu cuenta no tiene sujeto de aprendizaje',
      'Nadie ha abierto todavia un programa de estudio a tu nombre.');
    return;
  }
  // El programa NO se inventa ni se escribe a mano: sale de whoami.
  if (!quien.programas.length) {
    $el.innerHTML = avisoApr('Todavia no estas en ningun programa',
      'Tu sujeto existe, pero no tiene ningun pensum asignado.');
    return;
  }

  const bloques = [];
  for (const prog of quien.programas.slice(0, 3)) {
    const r = await LearningProfileApiClient.getProfile({ sujeto: quien.sujetoId, programa: prog });
    const cabecera = '<div style="color:#e1e4e8;font-weight:600;margin-bottom:8px">' + esc(prog) + '</div>';
    if (!r.ok) {
      bloques.push('<div style="margin-bottom:14px">' + cabecera
        + avisoApr('No pudimos traer este programa', r.detalle || r.motivo) + '</div>');
      continue;
    }
    bloques.push('<div style="margin-bottom:18px">' + cabecera
      + perfilAprHtml(r.perfil, prog)
      + sesionesAprHtml(r.sesiones)
      + observacionesAprHtml(r.pendientes)
      + '</div>');
  }
  $el.innerHTML = bloques.join('');
}

async function renderMemoriaTab($el, userId) {
  $el.innerHTML = '<span style="color:#8b949e">Cargando memoria del agente...</span>';
  try {
    const res = await AgentMemoryApiClient.getFacts(userId);
    const facts = res.ok ? (res.data?.data || []) : [];
    if (facts.length === 0) {
      $el.innerHTML = '<span style="color:#484f58">Sin hechos compilados. Usa Memory Compiler desde <a href="#/agent-quality" style="color:#58a6ff">#/agent-quality</a> tab Facts.</span>';
      return;
    }
    const types = { name: '\uD83D\uDC64', role: '\uD83D\uDCBC', preference: '\u2B50', skill: '\uD83D\uDEE0', project: '\uD83D\uDCC1', fact: '\uD83D\uDCCC', goal: '\uD83C\uDFAF', context: '\uD83C\uDF10' };
    $el.innerHTML = `
      <div style="font-weight:600;margin-bottom:8px;color:#58a6ff">Lo que Argemiro recuerda</div>
      <div style="display:flex;flex-wrap:wrap;gap:8px">
        ${facts.map(f => `
          <div style="background:#161b22;border:1px solid #30363d;border-radius:6px;padding:8px 12px;min-width:180px">
            <div style="font-size:0.875rem;color:#8b949e">${types[f.fact_type] || '?'} ${f.fact_type}</div>
            <div style="font-weight:600;margin-top:2px">${esc(f.fact_value)}</div>
            <div style="font-size:0.875rem;color:#484f58;margin-top:2px">${Math.round((f.confidence || 0) * 100)}% conf</div>
          </div>
        `).join('')}
      </div>`;
  } catch (err) {
    $el.innerHTML = `<span style="color:#f85149">Error: ${esc(err.message)}</span>`;
  }
}

// ─── Main render ─────────────────────────────────────────────────────────────

export async function renderUserList(container) {
  container.innerHTML = `
    <section class="artifact-view">
      <div class="artifact-header">
        <div>
          ${regresoHtml()}
          <h1>Usuarios</h1>
          <p class="subtitle">Gestion de acceso y progreso de skills</p>
        </div>
        <button class="btn-primary btn-sm" id="btn-new-user">+ Nuevo usuario</button>
      </div>

      <div id="nx-user-form-area" hidden>
        <form id="nx-user-form" class="inline-form">
          <input type="email" id="nx-user-email" placeholder="Email" required class="form-input">
          <input type="text" id="nx-user-name" placeholder="Nombre (opcional)" class="form-input form-input-sm">
          <select id="nx-user-role" class="form-input form-input-sm">
            ${ROLES.map(r => `<option value="${r}" ${r === 'user' ? 'selected' : ''}>${r}</option>`).join('')}
          </select>
          <button type="submit" class="btn-primary btn-sm">Crear</button>
          <button type="button" class="btn-ghost btn-sm" id="btn-cancel-user">Cancelar</button>
        </form>
        <p id="nx-user-form-error" class="auth-error" hidden></p>
      </div>

      <div class="artifact-list" id="nx-user-list">
        <p class="empty-state">Cargando usuarios...</p>
      </div>
    </section>`;

  setupFormHandlers();
  setupListHandlers();
  await loadUsers();
}

function setupFormHandlers() {
  const $btn = document.getElementById('btn-new-user');
  const $area = document.getElementById('nx-user-form-area');
  const $form = document.getElementById('nx-user-form');
  const $cancel = document.getElementById('btn-cancel-user');
  const $error = document.getElementById('nx-user-form-error');

  $btn.onclick = () => {
    $area.hidden = !$area.hidden;
    if (!$area.hidden) document.getElementById('nx-user-email').focus();
  };

  $cancel.onclick = () => { $area.hidden = true; };

  $form.onsubmit = async (e) => {
    e.preventDefault();
    $error.hidden = true;

    const email = document.getElementById('nx-user-email').value.trim();
    const displayName = document.getElementById('nx-user-name').value.trim();
    const role = document.getElementById('nx-user-role').value;

    if (!email) {
      $error.textContent = 'Email requerido';
      $error.hidden = false;
      return;
    }

    const result = await UserApiClient.create({ email, displayName, role });

    if (result.ok) {
      $area.hidden = true;
      $form.reset();
      await loadUsers();
    } else {
      $error.textContent = result.error;
      $error.hidden = false;
    }
  };
}

function setupListHandlers() {
  const $list = document.getElementById('nx-user-list');

  $list.addEventListener('change', async (e) => {
    if (!e.target.classList.contains('role-select')) return;
    const id = e.target.dataset.id;
    const role = e.target.value;
    if (!id || !role) return;

    e.target.disabled = true;
    const result = await UserApiClient.update(id, { role });
    if (result.ok) {
      await loadUsers();
    } else {
      avisar(`Error: ${result.error}`, { tono: 'error' });
      e.target.disabled = false;
    }
  });

  $list.addEventListener('click', async (e) => {
    const nameEl = e.target.closest('.editable-name');
    if (nameEl) {
      startInlineEdit(nameEl);
      return;
    }

    const btn = e.target.closest('.btn-delete');
    if (!btn) return;
    const id = btn.dataset.id;
    if (!await confirmar({ titulo: 'Desactivar este usuario', texto: 'Ya no podra entrar al panel.', aceptar: 'Desactivar usuario', peligro: true })) return;

    btn.disabled = true;
    const result = await UserApiClient.deactivate(id);
    if (result.ok) {
      await loadUsers();
    } else {
      avisar(`Error: ${result.error}`, { tono: 'error' });
      btn.disabled = false;
    }
  });
}

async function loadUsers() {
  const $list = document.getElementById('nx-user-list');
  if (!$list) return;

  $list.innerHTML = '<p class="empty-state">Cargando...</p>';

  const result = await UserApiClient.list(1, 50);

  if (!result.ok) {
    $list.innerHTML = `<p class="empty-state">Error: ${result.error}</p>`;
    return;
  }

  const payload = result.data?.data || result.data;
  const items = payload?.items || [];

  if (items.length === 0) {
    $list.innerHTML = '<p class="empty-state">No hay usuarios registrados</p>';
    return;
  }

  // SUJ-T2 (b): las personas primero; los agentes aparte, plegados y dichos como agentes.
  // SUJ-T8 (nexo-api 306): la frontera sale del campo sujeto; login compartido y cuentas sin persona van aparte.
  const { personas, agentes, cuentas } = separarPersonasDeAgentes(items);
  $list.innerHTML = `
    <div data-usuarios-personas>
      <div style="font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.04em;margin:4px 0 8px">Personas (${personas.length})</div>
      ${personas.map(userRow).join('') || '<p class="empty-state">No hay personas con acceso al panel</p>'}
    </div>
    ${cuentas.length ? `
    <details data-usuarios-cuentas style="margin-top:18px">
      <summary style="cursor:pointer;font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.04em">Cuentas que no son personas (${cuentas.length}) — logins del negocio y cuentas del sistema</summary>
      <div style="margin-top:8px">${cuentas.map((u) => `<div style="font-size:0.875rem;color:#6e7681;margin:6px 0 2px">${esc(porQueNoEsPersona(u))}</div>${userRow(u)}`).join('')}</div>
    </details>` : ''}
    ${agentes.length ? `
    <details data-usuarios-agentes style="margin-top:18px">
      <summary style="cursor:pointer;font-size:0.875rem;color:#8b949e;text-transform:uppercase;letter-spacing:0.04em">Agentes (${agentes.length}) — no son personas: no cuentan en fichas, asistencia ni capacitación</summary>
      <div style="margin-top:8px">${agentes.map(userRow).join('')}</div>
    </details>` : ''}`;

  // Fire-and-forget: load skill chips asynchronously
  loadSkillSummaries(items);

  // Click en fila: panel expandible con tabs Skills | Memoria
  $list.querySelectorAll('.user-row').forEach(row => {
    row.addEventListener('click', async (e) => {
      if (e.target.closest('.role-select') || e.target.closest('.btn-delete') ||
          e.target.closest('.editable-name') || e.target.closest('input') ||
          e.target.closest('.user-detail-panel')) return;

      const id = row.dataset.id;
      const existing = row.nextElementSibling?.classList?.contains('user-detail-panel')
        ? row.nextElementSibling : null;

      if (existing) { existing.remove(); return; }

      // Collapse others
      $list.querySelectorAll('.user-detail-panel').forEach(p => p.remove());

      const user = items.find(u => u.id === id);
      const isAgent = user ? isAgentUser(user) : false;
      // CTX-P2: la pestana de aprendizaje solo puede resolver el sujeto de
      // QUIEN TIENE LA SESION (la API lo saca del email de su token), asi que
      // hace falta saber si esta fila es la suya.
      const mio = correoDeLaSesion();
      const esMiFila = !!mio && String(user?.email || '').toLowerCase() === mio;

      const panel = document.createElement('div');
      panel.className = 'user-detail-panel';
      panel.style.cssText = 'background:#0d1117;border:1px solid #30363d;border-radius:0 0 6px 6px;margin-top:-1px;font-size:0.875rem';

      const tabStyle = 'padding:8px 16px;background:transparent;border:none;color:#8b949e;cursor:pointer;font-size:0.875rem;font-weight:600;border-bottom:2px solid transparent';
      const activeStyle = 'border-bottom-color:#58a6ff;color:#e1e4e8';

      // SUJ-T8: una persona tiene UNA ficha (la misma de Personas y Colaboradores). Los agentes, los
      // logins del negocio y las cuentas sin persona no (nexo-api 306: sujeto por fila).
      const ruta = user?.personaId ? rutaDeLaFicha('persona', user.personaId) : rutaDeLaFicha('usuario', id);
      const enlaceFicha = sujetoDe(user) !== 'persona' ? '' : `
        <div style="padding:8px 16px;border-bottom:1px solid #21262d">
          <a href="${ruta}" data-abrir-ficha style="color:#58a6ff;font-size:0.875rem">Abrir la ficha completa de la persona &rarr;</a>
        </div>`;
      panel.innerHTML = enlaceFicha + `
        <div style="display:flex;gap:0;border-bottom:1px solid #21262d">
          <button class="user-detail-tab" data-tab="skills" style="${tabStyle};${activeStyle}">${isAgent ? 'Badges' : 'Skills'}</button>
          <button class="user-detail-tab" data-tab="aprendizaje" style="${tabStyle}">Aprendizaje</button>
          <button class="user-detail-tab" data-tab="memoria" style="${tabStyle}">Memoria</button>
        </div>
        <div class="user-detail-content" style="padding:12px 16px">
          <span style="color:#8b949e">Cargando...</span>
        </div>`;

      row.after(panel);

      const $content = panel.querySelector('.user-detail-content');
      const $tabs = panel.querySelectorAll('.user-detail-tab');

      $tabs.forEach(tab => {
        tab.addEventListener('click', (ev) => {
          ev.stopPropagation();
          $tabs.forEach(t => { t.style.borderBottomColor = 'transparent'; t.style.color = '#8b949e'; });
          tab.style.borderBottomColor = '#58a6ff';
          tab.style.color = '#e1e4e8';
          if (tab.dataset.tab === 'skills') renderSkillsTab($content, id, isAgent);
          else if (tab.dataset.tab === 'aprendizaje') renderAprendizajeTab($content, esMiFila);
          else renderMemoriaTab($content, id);
        });
      });

      // Prevent clicks inside panel from collapsing
      panel.addEventListener('click', (ev) => ev.stopPropagation());

      // Load skills tab by default
      await renderSkillsTab($content, id, isAgent);
    });
  });
}

function startInlineEdit(el) {
  if (el.querySelector('input')) return;
  const id = el.dataset.id;
  const currentName = el.dataset.name;

  const input = document.createElement('input');
  input.type = 'text';
  input.value = currentName;
  input.className = 'form-input inline-edit-input';

  el.textContent = '';
  el.appendChild(input);
  input.focus();
  input.select();

  async function save() {
    const newName = input.value.trim();
    if (!newName || newName === currentName) {
      await loadUsers();
      return;
    }
    const result = await UserApiClient.update(id, { displayName: newName });
    if (result.ok) {
      await loadUsers();
    } else {
      el.textContent = currentName;
    }
  }

  input.addEventListener('blur', save);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') { e.preventDefault(); input.blur(); }
    if (e.key === 'Escape') { el.textContent = currentName; }
  });
}
